import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { AuthShell } from "../auth/components/AuthShell";
import { safeReturnTo } from "../auth/rules/returnTo";

/**
 * GUEST_ONLY_PATHNAMES are the auth pages that mean nothing to a signed-in user:
 * Ory refuses to mint their flows against a live session, so landing on one only
 * produces a `session_already_available` bounce. The guard sends those visitors
 * into the app instead.
 *
 * It is an ALLOWLIST, not an exemption list — a new auth route is unguarded until
 * it is deliberately added here, so the guard can never strand a page a signed-in
 * user legitimately needs. Deliberately absent:
 *
 * - `/verification` — the in-app verify-email banner opens it FOR a signed-in user
 *   (`/verification?autostart=true`); bouncing them makes verification unreachable.
 * - `/oauth2/consent` — it REQUIRES a session to grant the connector.
 * - `/error` — a terminal page any visitor may land on.
 */
const GUEST_ONLY_PATHNAMES: ReadonlySet<string> = new Set(["/login", "/recovery", "/registration"]);

/**
 * guestGuardSearch is the slice of the tree-wide router search the guard reads. The
 * layout owns no validateSearch of its own (each child route declares its own
 * schema), so it parses the loose location search the same way the _app layout
 * reads the open-itinerary id. Every field is `.catch`-defaulted and the object
 * itself falls back to empty, because a guard must never throw on a search shape it
 * did not expect — the worst outcome of an unreadable search is that the visitor
 * sees the auth page they asked for.
 */
const guestGuardSearch = z
  .object({
    aal: z.string().optional().catch(undefined),
    login_challenge: z.string().optional().catch(undefined),
    // The router parses `refresh=true` into a boolean, but a hand-written URL can
    // leave it a string; both spellings carry the same intent.
    refresh: z.union([z.boolean(), z.string()]).optional().catch(undefined),
    return_to: z.string().optional().catch(undefined),
  })
  .catch({});

/**
 * GuestGuardSearch is the parsed guard slice, derived from guestGuardSearch.
 */
type GuestGuardSearch = z.infer<typeof guestGuardSearch>;

/**
 * isDeliberateAuthVisit reports whether a signed-in user is on an auth page ON
 * PURPOSE, in which case the guard must stand down:
 *
 * - `login_challenge` — the user is mid-OAuth round-trip (Ory → managed login →
 *   Kratos → here). Redirecting into the app DROPS the Hydra challenge and strands
 *   the connector flow.
 * - `refresh` — a re-authentication before a sensitive action; the user is signed
 *   in by definition.
 * - `aal` — a step-up to a second factor, likewise only meaningful with a session.
 */
const isDeliberateAuthVisit = (search: GuestGuardSearch): boolean =>
  search.login_challenge !== undefined ||
  search.aal !== undefined ||
  search.refresh === true ||
  search.refresh === "true";

/**
 * normalizePathname strips a trailing slash so `/login/` matches `/login` in the
 * guest-only allowlist.
 */
const normalizePathname = (pathname: string): string => pathname.replace(/\/+$/, "") || "/";

/**
 * Route is the pathless _auth layout: the responsive split-screen shell shared by
 * every auth page (login/registration/verification/recovery/error). The leading
 * underscore keeps /_auth out of the URL — children render inside the <Outlet>.
 *
 * Its beforeLoad is the guest guard, the mirror of the _app gate: where _app sends
 * anonymous visitors OUT to /login, this sends authenticated visitors IN to the app.
 * Without it a signed-in user opening /login mints a flow, Ory answers
 * `session_already_available`, and the flow hook resolves that with a FULL-PAGE
 * navigation — a login-form flash at best, and a reload cycle whenever the app and
 * Ory disagree about whether a session exists. Redirecting here resolves it before
 * a component mounts, client-side, with no document request at all.
 */
export const Route = createFileRoute("/_auth")({
  beforeLoad: ({ context, location }) => {
    if (!context.session || !GUEST_ONLY_PATHNAMES.has(normalizePathname(location.pathname))) {
      return;
    }

    const search = guestGuardSearch.parse(location.search);
    if (isDeliberateAuthVisit(search)) {
      return;
    }

    // The destination rides through the shared validator, so a return_to pointing at
    // another origin or back at an auth page degrades to the studio rather than being
    // followed into an open redirect or a cycle.
    throw redirect({ href: safeReturnTo(search.return_to) });
  },
  component: AuthLayout,
});

function AuthLayout() {
  return (
    <AuthShell>
      <Outlet />
    </AuthShell>
  );
}
